import DetailViewController from 'app/src/controllers/components/DetailViewController';
import GatewayDetailView from 'app/src/views/settings/paymentProcessing/GatewayDetailView';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Gateway from 'app/src/data/models/Gateway';
import CommonData from 'app/src/data/CommonData';
import Shop from 'app/src/data/models/Shop';
import Localize from 'app/src/localize/Localize';
import Format from 'app/src/data/Format';
import CCTerminalCollection from 'app/src/data/models/CCTerminalCollection';
import CCTerminal from 'app/src/data/models/CCTerminal';
import Navigation from 'app/Navigation';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import Analytics from 'shared_modules/Analytics';
import Modules from 'shared_modules/Modules';
import $ from 'jquery';

export default class GatewayDetailViewController extends DetailViewController {
  /**
   * Constructs the Controller by setting up the viewData for the Add Gateway page
   *
   * gateways - This is all the possible gateways for this particular account. Retrieved from the Gateway endpoint
   * gatewaySelected - This value is taken from the gateway dropdown select, and determines
   *                   which form elements are shown
   * shops - this is the shop information for populating the checkboxes so the user
   *         is able to assign stores to the Gateway
   * paymentMapping - This is a grouping of names for paymentTypes, Credit, Debit, and the UserDefined ones.
   */
  constructor(gatewayConfig, gateway) {
    if (!(gatewayConfig instanceof GatewayConfig)) {
      throw 'A GatewayConfig must be specified when constructing GatewayDetailViewController';
    }

    if (!(gateway instanceof Gateway)) {
      throw 'A Gateway (Setting) must be specified when constructing GatewayDetailViewController';
    }

    let paymentMapping = [];
    let userDefinedPayments = PaymentTypeManager.getUserDefinedPaymentTypes();
    let creditCardPayments = PaymentTypeManager.allAvailableTypes.filter({
      type: 'credit card',
    });

    creditCardPayments.forEach(paymentType => {
      paymentMapping.push({
        name: paymentType.get('name'),
        paymentTypeID: paymentType.get('paymentTypeID'),
      });
    });

    userDefinedPayments.forEach(paymentType => {
      paymentMapping.push({
        name: paymentType.get('name'),
        paymentTypeID: paymentType.get('paymentTypeID'),
      });
    });

    // The Gateway we're currently looking at (if we're editing a gateway
    // rather than adding a new one)
    let gatewayName = null;
    let gatewayID = gatewayConfig.get('gatewayID') ? gatewayConfig.get('gatewayID') : null;
    if (gatewayID) {
      // Convert the gatewayID to a human-readable gateway Name
      // e.g. vantiv-canada => Vantiv
      gateway.get('gateway').forEach(gatewayDetails => {
        if (gatewayDetails.gatewayID === gatewayID) {
          gatewayName = gatewayDetails.name;
        }
      });
    }

    // The Lightspeed Payments gateway should not appear in any forms
    gateway.set(
      'gateway',
      gateway
        .get('gateway')
        .filter(
          paymentGateway =>
            !paymentGateway ||
            paymentGateway.gatewayID !== GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
        )
    );
    // initialize the controller's managed view
    super(
      new GatewayDetailView({
        model: gatewayConfig,
        viewData: {
          gatewayID,
          gatewayName,
          gateways: gateway.toJSON(),
          shops: CommonData.shops.toJSON(),
          paymentMapping,
          swipersModuleEnabled: Modules.hasModule('payments.settings.allowswipers'),
        },
      }),
      {
        isNew: gatewayConfig.isNew(),
      }
    );

    this._gatewayConfig = gatewayConfig;
    this._shops = CommonData.shops;
  }

  /**
   * Attach the DOM.
   * If it's for Editing a Gateway, show the Delete Button.
   * And set the form to the Current Gateway values.
   *
   * Calling super.show() here to set call order so that the DOM is loaded before view
   * actions are taken on them.
   */
  show() {
    super.show();

    if (!this._gatewayConfig.isNew()) {
      this._detailView.setFormFieldValues(this._gatewayConfig);
    }
  }

  /**
   * Save Delegate Method
   *
   * Calls to retrieve the Form Field Values from the View, and then saves it.
   * If the save is successful, then calls to assign the new Gateway to the Shops selected if any.
   *
   * @return {Promise}    Resolves if the configuration was successfully saved and rejects otherwise.
   */
  save() {
    let formIsValid = this._detailView.formFieldValidation();
    let deferred = $.Deferred();

    if (formIsValid) {
      this._setGatewayConfigValues();

      this._gatewayConfig
        .save()
        .done(response => {
          // Save the user's list of gateways to Mixpanel
          Analytics.submitGatewaysEvent();

          flashNotifier('success', Localize.translate('Gateway Saved Successfully'), 4);
          this._setShopGatewayValues(response.GatewayConfig.gatewayConfigID);
          Navigation.navigateToClientUrl('settings/payment_processing');

          deferred.resolve();
        })
        .fail(jqXHR => {
          PaymentServiceAlert.alert(jqXHR);
          deferred.reject();
        });
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Delete Delegate Method
   *
   * @return {Promise}    Resolves if the configuration was successfully saved and rejects otherwise.
   */
  remove() {
    let deferred = $.Deferred();
    let gatewayConfig = this._gatewayConfig;

    // Check if the gateway is assigned to any terminals
    $.when(PaymentProvider.gatewayHasTerminals(gatewayConfig)).done(gatewayHasTerminalsAssigned => {
      if (gatewayHasTerminalsAssigned) {
        // If so, don't let the user delete the Gateway
        alert(
          Localize.translate(
            'Remove all attached Payment Terminals from this Payment Gateway first.'
          )
        );
        deferred.resolve();
      } else {
        // Otherwise, delete the gateway if they confirm
        let confirmedText =
          'Are you sure you wish to delete this Payment Gateway?\n\nAll settings will be lost';
        if (PaymentProvider.gatewaySupportsRefundByReference(gatewayConfig)) {
          confirmedText += ' and you won’t be able to process refunds';
        }
        confirmedText += '.';

        let confirmed = window.confirm(Localize.translate(confirmedText));

        if (confirmed) {
          $.when(gatewayConfig.destroy())
            .done(() => {
              Analytics.submitGatewaysEvent();
              Navigation.navigateToClientUrl('settings/payment_processing');
            })
            .fail(jqXHR => {
              PaymentServiceAlert.alert(jqXHR);
            })
            .always(() => {
              deferred.resolve();
            });
        } else {
          deferred.resolve();
        }
      }
    });

    return deferred;
  }

  /**
   * Retrieves the Form Field Values from the view and sets it to the gatewayConfig model
   *
   * @private
   */
  _setGatewayConfigValues() {
    let formFieldValues = this._detailView.getFormFieldValues();

    this._gatewayConfig.set(formFieldValues);
  }

  /**
   * Retrieves the Shops selection checkboxes from the view,
   * then assigns the new Gateway to the shops checked.
   *
   * @param {string|number} gatewayConfigID   The gateway ID
   * @private
   */
  _setShopGatewayValues(gatewayConfigID) {
    let selectedShops = this._detailView.getShopFormFieldValues();
    let shopsJSON = this._shops.toJSON();

    shopsJSON.forEach(shop => {
      if (selectedShops[`shop_${shop.shopID}`]) {
        // TODO: Until the gateway configs have been completely migrated to the payment service
        // we need to support both gatewayConfigID and gatewayConfigID.
        if (gatewayConfigID && gatewayConfigID.match && gatewayConfigID.match(/-/)) {
          shop.gatewayConfigID = gatewayConfigID;
        } else {
          shop.ccGatewayID = Format.parseInt(gatewayConfigID);
        }
        delete shop.CCGateway;
        let shopModel = new Shop(shop);
        shopModel
          .save()
          .done(() => {})
          .fail(jqXHR => {
            PaymentServiceAlert.alert(jqXHR);
          });
      }
    });
  }
}
